import { Button, Card, Col, Divider, Flex, Form, Radio, RadioChangeEvent, Row, Select, Typography } from 'antd';
import { Fragment, FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultOptionType } from 'antd/es/select';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// components
import FormElement from 'components/formElement/formElement';
import FileInput from 'components/fileInput/fileInput';
import ListButton from 'components/listButton/listButton';
import Icon from 'utils/icon';
import CompoundElement from 'components/compoundElement/compoundElement';
import DisabledElement from 'components/disabledElement/disabledElement';
import StatusComponent from 'components/status/status';
import PageTitle from 'components/title/title';
import BackButton from 'components/backButton/backButton';
import ComponentHeader from 'components/componentHeader/componentHeader';

// services

// styles

// models
import { Suspend, SuspendType } from '../models/suspend';
import { onGetCompanySuspendRequestById } from '../services/suspendServices';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import NotificationService from 'core/services/notification';
import { isRTL } from 'core/services/language';
import FilePreview from 'components/filePreview/filePreview';
import { formatFileSize } from 'utils/FileSize';
import { dateFormat } from 'utils/dateFormat';
import InfoTooltip from 'components/info/info';

interface SuspendViewPageProps {}

const CompanySuspendViewPage: FunctionComponent<SuspendViewPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const [suspendForm] = Form.useForm();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [suspend, setSuspend] = useState<Suspend>();

	useEffect(() => {
		getSuspendDetails();
	}, []);

	const getSuspendDetails = async () => {
		try {
			setIsLoading(true);

			const response = await onGetCompanySuspendRequestById(id as string);
			setSuspend(response);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			navigate('/dashboard/actions/suspend');
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			{isLoading ? (
				<LoadingComponent />
			) : (
				<Form form={suspendForm} onFinish={() => {}}>
					<Card>
						<ComponentHeader title={t('ACTIONS.SUSPEND')} />

						{suspend?.type == 'General' ? (
							<div>
								<p className="fw-semibold mb-0">{t('ACTIONS.GENERAL')}</p>
							</div>
						) : (
							<div>
								<p className="fw-semibold mb-0">{t('ACTIONS.SPECIFIC')}</p>
							</div>
						)}
					</Card>

					<Card className="mt-4">
						<Row gutter={[16, 16]}>
							<Col span={24}>
								<h5>{t(`ACTIONS.${suspend?.type.toUpperCase()}_SUSPENSION`)}</h5>
							</Col>

							{suspend?.type === 'Specific' && (
								<>
									<Col span={24} md={12}>
										<DisabledElement label={t('COMMON.NATIONAL_ID')} value={suspend?.company_shareholder.national_id} />
									</Col>

									<Col span={24} md={12}>
										<DisabledElement
											label={t('COMMON.NAME')}
											value={isRTL() ? suspend.company_shareholder.name : (suspend.company_shareholder.name_en ?? suspend.company_shareholder.name)}
										/>
									</Col>

									{suspend.suspended_common_shares_count ? (
										<Col span={24} md={12}>
											<DisabledElement label={t('ACTIONS.SUSPENDED_COMMON_SHARES_COUNT')} value={suspend.suspended_common_shares_count} />
										</Col>
									) : null}

									{suspend.suspended_preferred_shares_count ? (
										<Col span={24} md={12}>
											<DisabledElement label={t('ACTIONS.SUSPENDED_PREFERRED_SHARES_COUNT')} value={suspend.suspended_preferred_shares_count} />
										</Col>
									) : (
										<Col span={24} md={12}></Col>
									)}
								</>
							)}

							<Col span={24} md={12}>
								<Flex gap={0} align="center" style={{ marginBottom: 5 }}>
									<Typography style={{ marginBottom: 0, fontSize: 15 }}>{t('ACTIONS.SUSPEND_REASON')}</Typography>
									<InfoTooltip title={t('ACTIONS.SUSPEND_REASON_TOOLTIP')} />
								</Flex>
								<Select
									mode="multiple"
									disabled
									style={{ display: 'block', width: 'var(--inputWidth)', maxWidth: '100%' }}
									value={suspend?.types.map((item: SuspendType) => ({
										label: isRTL() ? item.translated_title.ar : item.translated_title.en,
										value: item.id,
									}))}
								/>
							</Col>

							<Col span={24} md={12}></Col>

							<Col span={24} md={12}>
								<DisabledElement label={t('COMMON.ARABIC_DESC')} value={suspend?.translated_description.ar} />
							</Col>

							<Col span={24} md={12}>
								<DisabledElement label={t('COMMON.ENGLISH_DESC')} value={suspend?.translated_description.en} />
							</Col>

							<Col span={24}>
								<Divider className="my-0" />
							</Col>

							<Col span={24}>
								<Flex gap={0} align="center" style={{ marginBottom: 5 }}>
									<h5 className="mb-0">{t('ACTIONS.UPLOADED_DOCUMENTS')}</h5>
									<InfoTooltip title={t('ACTIONS.UPLOAD_DOCUMENTS_TOOLTIP')} />
								</Flex>{' '}
							</Col>

							{suspend?.files?.map((file, index) => (
								<Col key={index} span={24}>
									<FilePreview name={file.title} downloadLink={file.download_link} data={formatFileSize(file.size_in_kilobyte)} />
								</Col>
							))}
						</Row>
					</Card>

					{suspend?.status === 'Canceled' && (
						<Card className="mt-4">
							<ComponentHeader
								title={t('ACTIONS.SUSPEND_ACTION_CANCELLED')}
								subtitle={dateFormat(suspend?.cancelled_at)}
								subtitleStyle={{ color: 'red', fontWeight: 500 }}
							/>

							<DisabledElement label={t('ACTIONS.SUSPEND_REASON')} value={suspend?.cancelled_reason} />

							<FilePreview
								name={t('ACTIONS.SUPPORTED_DOCUMENTS')}
								downloadLink={suspend?.files?.[0]?.download_link}
								data={formatFileSize(suspend?.files?.[0]?.size_in_kilobyte)}
							/>
						</Card>
					)}
				</Form>
			)}
		</>
	);
};

export default CompanySuspendViewPage;
